<script setup lang="ts">
import {computed, nextTick, onMounted, ref, useId, watch} from 'vue';
import {QVirtualScroll} from 'quasar';
import {useI18n} from 'vue-i18n';
import dayjs from 'dayjs';
import type {DayMoodDTO, MoodDTO} from '@/ts/types/dto/journals-dto';
import {useMoods} from '@/ts/composables/pure/use-moods';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import {getDayOfWeekIndex, getStartOfWeek} from '@/ts/utils/pure-functions';

/**
 * The journal's mood map: one circle per calendar day, coloured by the mood recorded that day
 * (IPD-3107). It replaces the averaged mood trend — a mood is a name, not a point on a scale, so
 * there is nothing here to average.
 *
 * Four layouts, chosen by the period the page is showing:
 *  - `week`  — the week the period starts in, so a single day still reads in its week
 *  - `span`  — exactly the days between the bounds, weekday-aligned, for a trailing window that
 *              lines up with neither a calendar week nor a month
 *  - `range` — a block per calendar month, scrolling sideways
 *
 * A day is drawn in one of three states: coloured when it carries a mood, plain when it is a past
 * day the user did not log, and outlined when it is still to come.
 */
const props = defineProps<{
  /** Only days carrying a mood; absent days are the untracked ones. */
  days: DayMoodDTO[];
  mode: 'week' | 'span' | 'range';
  /** i18n key for the header count, chosen by the period rather than by the layout. */
  countLabelKey: string;
  /** yyyy-MM-dd, inclusive. */
  periodStart: string;
  /** yyyy-MM-dd, inclusive. */
  periodEnd: string;
  /**
   * Moods the filter bar is narrowing the entry list to. The map does NOT drop the other days —
   * it dims them. The list answers "which entries match", the map keeps answering "how has the
   * period gone", and losing the unmatched days would take the second question away.
   *
   * Empty means nothing is being narrowed, so nothing dims.
   */
  highlightMoods?: MoodDTO[];
}>();

const emit = defineEmits<{
  /** A day with an entry behind it was activated. `yyyy-MM-dd`. */
  daySelect: [date: string];
}>();

const DATE_FORMAT = 'YYYY-MM-DD';
/**
 * The pitch from one month block to the next, which the virtual scroller needs up front to size
 * its runway before a single block has been rendered.
 *
 * 218 for the day grid — seven 26px columns and six 6px gaps — plus the 20 separating it from the
 * next block. `.mm-group` pins the same total as its own width rather than leaving it to the
 * content, because the scroller corrects its runway against whatever the rendered blocks measure:
 * a block measuring anything but this number makes the runway drift as it is scrolled.
 *
 * Kept in step with `$mm-day-gap` and `$mm-block-width` in this file's styles by hand — a style
 * cannot be read from here, so a change there has to be repeated here.
 */
const MONTH_BLOCK_WIDTH = 238;
// In dayjs `.day()` order, Sunday first, so `weekStartsOn` indexes straight into it.
const DAY_LABEL_KEYS = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];

const {t} = useI18n();
const datePatterns = useProfileDateFormat();
const {getMoodTint} = useMoods();

/** The weekday names over the columns, from the day the reader's week starts on. */
const weekdayKeys = computed(() => Array.from(
  {length: 7},
  (_, i) => DAY_LABEL_KEYS[(i + datePatterns.value.weekStartsOn) % 7],
));

interface Cell {
  key: string;
  /** Undefined on an untracked or future day. */
  mood?: MoodDTO;
  isFuture: boolean;
  label: string;
}

interface Group {
  key: string;
  label: string;
  /** 1-7, the grid column the 1st of the month sits in, so rows line up as weeks. */
  startColumn: number;
  cells: Cell[];
}

const moodByDate = computed(() => new Map(props.days.map(d => [d.date, d.mood])));

/**
 * Whether the map covers more than one calendar year, which decides whether labels name it.
 *
 * Within a single year the year is noise — "Sep" above a column of September days already says
 * everything. Over a longer history the same "Sep" recurs down the scroll with nothing to tell
 * 2023's from 2025's, and because the month blocks are recycled the reader cannot count back to a
 * January that is off-screen. So every label takes the year as soon as there is a second one.
 */
const spansMultipleYears = computed(
  () => dayjs(props.periodStart).year() !== dayjs(props.periodEnd).year(),
);
const yearPart = computed<Intl.DateTimeFormatOptions>(
  () => (spansMultipleYears.value ? {year: 'numeric'} : {}),
);

/**
 * Weekday and month names come from `Intl` rather than dayjs, because no dayjs locale is loaded
 * anywhere in the app — `format('MMMM')` would print English month names on the German site. The
 * locale carries the profile's country too, so an English reader in Germany gets "Mon, 19 Jan"
 * rather than the American order (IPD-3229). Built once per language instead of per cell: the year
 * layout draws over three hundred of them.
 */
const dayLabelFormat = computed(
  () => new Intl.DateTimeFormat(datePatterns.value.intlLocale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...yearPart.value,
  }),
);
const monthShortFormat = computed(
  () => new Intl.DateTimeFormat(datePatterns.value.intlLocale, {month: 'short', ...yearPart.value}),
);

const today = computed(() => dayjs().format(DATE_FORMAT));

/**
 * A month block's days, restated as the type they already have.
 *
 * The virtual scroller hands its slot item back untyped, and a `v-for` over an untyped value types
 * its index as `string | number` — the key it would yield if the value turned out to be an object.
 * `monthCellStyle` needs the number, so the loop is given something of known type to walk.
 */
function cellsOf(group: Group): Cell[] {
  return group.cells;
}

function buildCell(date: dayjs.Dayjs): Cell {
  const key = date.format(DATE_FORMAT);
  return {
    key,
    mood: moodByDate.value.get(key),
    isFuture: key > today.value,
    label: dayLabelFormat.value.format(date.toDate()),
  };
}

/**
 * The week the period starts in, from the day the reader's country starts its weeks on (Sunday in
 * the States, Monday elsewhere), so the columns always line up with their labels.
 */
const weekCells = computed<Cell[]>(() => {
  const first = getStartOfWeek(dayjs(props.periodStart), datePatterns.value.weekStartsOn);
  return Array.from({length: 7}, (_, i) => buildCell(first.add(i, 'day')));
});

/**
 * Every day of the period, for a trailing window like "the last 7 days" or "the last 30 days".
 *
 * Drawn day-for-day rather than snapped to a calendar week or month, because the window is not one
 * of those: a map that padded it out to whole months would colour days the list beside it excludes,
 * and the header's count would stop matching the entries on screen.
 */
const spanCells = computed<Cell[]>(() => {
  const start = dayjs(props.periodStart);
  const length = dayjs(props.periodEnd).diff(start, 'day') + 1;
  return Array.from({length: Math.max(length, 1)}, (_, i) => buildCell(start.add(i, 'day')));
});

/** The grid column the window's first day belongs in, so its rows line up as weeks. */
const spanStartColumn = computed(() => getDayOfWeekIndex(dayjs(props.periodStart), datePatterns.value.weekStartsOn) + 1);

/**
 * One group per calendar month the period touches, each holding the days of it inside the period.
 *
 * The first block starts at the period, not at the 1st. A trailing window begins mid-month, and
 * the days before it were never asked for — so drawing them showed a day the reader had recorded a
 * mood on as a day they had recorded nothing, which is the one thing a map of someone's health
 * data must not do. On "last year" that was up to thirty days of real moods drawn as blanks.
 */
const monthGroups = computed<Group[]>(() => {
  const groups: Group[] = [];
  const start = dayjs(props.periodStart);
  const end = dayjs(props.periodEnd);
  let cursor = start.startOf('month');

  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    const month = cursor;
    const firstDay = month.isSame(start, 'month') ? start.date() : 1;
    const first = month.date(firstDay);
    groups.push({
      key: month.format('YYYY-MM'),
      label: monthShortFormat.value.format(month.toDate()),
      // The first day of the reader's week = column 1, so each row of the grid really is a week and
      // the same weekday sits in the same column from one month block to the next. Taken from the
      // first day the block draws rather than from the 1st, which is not always the same day.
      startColumn: getDayOfWeekIndex(first, datePatterns.value.weekStartsOn) + 1,
      cells: Array.from(
        {length: month.daysInMonth() - firstDay + 1},
        (_, i) => buildCell(first.add(i, 'day')),
      ),
    });
    cursor = cursor.add(1, 'month');
  }
  return groups;
});

/**
 * The cells this layout actually draws. The count and the filter are both derived from these
 * rather than from the raw response, so the header can never claim more days than the map shows —
 * a response covering a wider span than the period on screen stays consistent either way.
 */
const visibleCells = computed<Cell[]>(() => {
  if (props.mode === 'week') {
    return weekCells.value;
  }
  if (props.mode === 'span') {
    return spanCells.value;
  }
  return monthGroups.value.flatMap(group => group.cells);
});

const trackedCount = computed(() => visibleCells.value.filter(cell => cell.mood).length);

// Keyed off the period, not the layout: "Today" and "Last week" both draw a week, but only one of
// them is *this* week, and the header would otherwise say so about both.
const countLabel = computed(
  () => t(props.countLabelKey, trackedCount.value, {named: {count: trackedCount.value}}),
);

/**
 * A day the mood filter is currently pushing into the background.
 *
 * Only days that recorded a mood the filter excludes. An untracked day carries no mood to include
 * or exclude, and a future day cannot carry one at all, so dimming those said nothing and left a
 * filtered week as a single visible circle among six ghosts — the opposite of keeping the shape of
 * the period readable.
 */
function isDimmed(cell: Cell): boolean {
  const highlight = props.highlightMoods ?? [];
  return highlight.length > 0 && Boolean(cell.mood) && !highlight.includes(cell.mood!);
}

function cellStyle(cell: Cell): Record<string, string | number> {
  return cell.mood ? {background: getMoodTint(cell.mood)} : {};
}

/** The 1st of a month also carries the offset that puts it under its own weekday. */
function monthCellStyle(cell: Cell, index: number, startColumn: number): Record<string, string | number> {
  return index === 0 ? {...cellStyle(cell), gridColumnStart: startColumn} : cellStyle(cell);
}

/** What a circle says about its day: the mood recorded, or why there is none. */
function cellNote(cell: Cell): string {
  if (cell.isFuture) {
    return t('mood_map_upcoming');
  }
  return cell.mood ? t(`mood_${cell.mood}`) : t('mood_map_untracked');
}

/**
 * The same two facts as one phrase, for each cell's `aria-label`.
 *
 * The bubble stacks them, but a label read aloud is a sentence, so they are joined by a comma —
 * not a dash, which sets two halves up as being contrasted.
 */
function cellTitle(cell: Cell): string {
  return `${cell.label}, ${cellNote(cell)}`;
}

/** A day worth opening: one the reader recorded a mood on, so there is an entry behind it. */
function isSelectable(cell: Cell): boolean {
  return Boolean(cell.mood) && !cell.isFuture;
}

/**
 * What a circle is as an element: a control when there is an entry behind it, a picture when not.
 *
 * Bindings rather than two branches in the template, because all three layouts draw the same
 * circle and a second copy of it in each of them is three places for them to drift apart.
 */
/** The map's own element, which the keyboard handlers and the tooltip both measure against. */
const root = ref<HTMLElement | null>(null);
/** The month-block scroller, so a day in a recycled block can be scrolled back into view. */
const plotArea = ref<QVirtualScroll | null>(null);

const instanceId = useId();
const activeDay = ref<string | null>(null);

/**
 * The map's single tab stop: the day the arrow keys are on.
 *
 * Every circle carries `tabindex`, and exactly one of them carries 0 — so Tab reaches the map once
 * and the arrows move about inside it, rather than the map spending a tab stop per day. A year is
 * several hundred days, and tabbing through those to reach whatever follows the map would be worse
 * than not reaching them at all.
 *
 * The day held in `activeDay` is only used while the map is still drawing it. Changing the period
 * redraws the map around a different span, and a stop pointing at a day that is no longer there
 * would leave every circle at -1 — the map silently out of the tab order, with no keyboard way
 * back into it. Otherwise it opens on the most recent day that has actually happened: the end the
 * map scrolls to, and never one of the outlined days still to come, which can be no use to anyone
 * who lands on them.
 */
const tabStop = computed(() => {
  const cells = visibleCells.value;
  const active = activeDay.value;
  if (active !== null && cells.some(cell => cell.key === active)) {
    return active;
  }
  return cells.findLast(cell => !cell.isFuture)?.key ?? cells[cells.length - 1]?.key ?? null;
});

/**
 * Every day is a button, whether or not it has an entry behind it.
 *
 * The days with nothing to open say so with `aria-disabled` rather than by being a different kind
 * of element. They stay focusable, so the arrows walk every day rather than skipping from mood to
 * mood — "Nothing recorded" is an answer, and a reader moving through a week should hear the quiet
 * days as well as the logged ones.
 *
 * They were pictures (`role="img"`) until review pointed out what that costs: a screen reader
 * stays in browse mode on a non-interactive element and keeps the arrow keys for its own cursor,
 * so the navigation below never ran for the readers the spoken hint is written for — and it did
 * run on the logged days, which are buttons, making the same widget behave two ways.
 */
/* eslint-disable @typescript-eslint/naming-convention -- these are HTML attributes, not properties */
function cellAttrs(cell: Cell): Record<string, unknown> {
  const attrs: Record<string, unknown> = {
    type: 'button',
    tabindex: cell.key === tabStop.value ? 0 : -1,
    // Spoken when focus lands on a day, which is the moment the keys are worth knowing about. On
    // the section it was never read out: a region does not pass its description to its children.
    'aria-describedby': `mood-map-keys-${instanceId}`,
    'data-day': cell.key,
  };
  // Left off entirely rather than set to nothing, so a day that opens carries no state at all.
  if (!isSelectable(cell)) {
    attrs['aria-disabled'] = 'true';
  }
  return attrs;
}
/* eslint-enable @typescript-eslint/naming-convention */

/** A week's worth of days, which is what the vertical arrows step by. */
const DAYS_IN_WEEK = 7;

/** How many frames to wait for a scrolled-to month block to appear before giving up. */
const RENDER_WAIT_FRAMES = 8;

async function nextFrame(): Promise<void> {
  await new Promise<void>(resolve => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

/**
 * Puts focus on a day, and makes sure it stays there.
 *
 * A day is found by its date rather than held as an element reference, because the element it had
 * a moment ago may since have been recycled into another month. Two things follow from that, both
 * of which this loop is for. A day in a month the scroller has just been sent to is not in the
 * document when the Vue tick resolves, since the scroller re-renders on the browser's schedule
 * rather than Vue's. And the node it first draws can be replaced a frame later as the slice
 * settles, which takes the focused element away and drops focus back to the scroll container.
 *
 * So focus is set, given a frame, and set again on whatever is drawing that day now — until it
 * sticks, or until the frames run out and it gives up rather than looping for ever.
 */
async function focusDay(key: string): Promise<void> {
  activeDay.value = key;
  if (props.mode === 'range') {
    const monthIndex = monthGroups.value.findIndex(group => group.cells.some(cell => cell.key === key));
    if (monthIndex >= 0) {
      plotArea.value?.scrollTo(monthIndex);
    }
  }
  await nextTick();

  for (let attempt = 0; attempt < RENDER_WAIT_FRAMES; attempt += 1) {
    // A held-down arrow key starts a move before this one has finished. The newer move is the one
    // the reader wants, so this drops out rather than dragging focus back to its own day.
    if (activeDay.value !== key) {
      return;
    }
    const cell = root.value?.querySelector<HTMLElement>(`[data-day="${key}"]`);
    cell?.focus();
    // eslint-disable-next-line no-await-in-loop -- each frame has to be waited for in turn
    await nextFrame();
    if (cell && document.activeElement === cell) {
      return;
    }
  }
}

/**
 * The arrows, and Home and End.
 *
 * Sideways moves a day at a time; up and down move a week, because the map is laid out in weeks
 * and the same weekday sits in the same column. Bound on the section rather than on every circle,
 * and it only acts when the key came from one of them.
 */
function onKeydown(event: KeyboardEvent): void {
  // A composite widget owns the plain arrow keys, not the modified ones: Alt or Cmd with an arrow
  // is the browser's Back, and Ctrl or Cmd with Home, End or an arrow jumps the document.
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return;
  }
  const from = (event.target as HTMLElement | null)?.closest?.('.mm-cell')?.getAttribute('data-day');
  if (!from) {
    return;
  }
  const days = visibleCells.value;
  // Counted from the day the map has moved to, not from the element holding focus. Moving focus
  // takes a frame or more in the scrolling layout, so a second key press can arrive while the
  // first is still in flight — and stepping from the element it has not left yet would lose it.
  const at = days.findIndex(cell => cell.key === (activeDay.value ?? from));
  if (at < 0) {
    return;
  }

  const steps: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -DAYS_IN_WEEK,
    ArrowDown: DAYS_IN_WEEK,
  };
  let to: number;
  if (event.key in steps) {
    to = at + steps[event.key];
  } else if (event.key === 'Home') {
    to = 0;
  } else if (event.key === 'End') {
    to = days.length - 1;
  } else {
    return;
  }

  // Held back from the page and from the strip, both of which would otherwise scroll.
  event.preventDefault();
  const target = days[Math.min(Math.max(to, 0), days.length - 1)];
  if (!target) {
    return;
  }
  // Up and down are a row of the grid the reader is looking at, and in the scrolling layout each
  // month is its own seven-column grid. A week's step out of the block lands in the next month —
  // which is drawn to the right and, on that block's own rows, higher up, so "down" would move the
  // focus ring up and across. Kept inside the month instead: the bottom row simply stops.
  const verticalStep = event.key === 'ArrowUp' || event.key === 'ArrowDown';
  if (props.mode === 'range' && verticalStep && target.key.slice(0, 7) !== days[at].key.slice(0, 7)) {
    return;
  }
  void focusDay(target.key);
}

/**
 * Keeps the tab stop where the reader left it, however they got there — an arrow key, a click, or
 * Tab itself. Without this, leaving the map and coming back would land on the most recent day
 * again rather than on the day they were looking at.
 */
function onFocusIn(event: FocusEvent): void {
  const day = (event.target as HTMLElement | null)?.closest?.('.mm-cell')?.getAttribute('data-day');
  if (day) {
    activeDay.value = day;
  }
}

function selectDay(cell: Cell): void {
  if (isSelectable(cell)) {
    emit('daySelect', cell.key);
  }
}

/**
 * The tooltip. A circle carries a date and a mood name that appear nowhere else on the map, so
 * without one the whole chart is ten shades of pastel and nothing to read. The native `title`
 * attribute is not enough: it waits about a second, cannot be styled, and never shows on touch.
 *
 * Positioned against the map rather than the page, so it lands over its own circle wherever the
 * card sits. It does not track a sideways scroll of the plot area — the bubble is dismissed on
 * pointer-leave, and scrolling the strip takes the pointer off the circle that opened it.
 *
 * Pointer events rather than mouse ones, so a tap opens it too — and focus opens it as well, so a
 * sighted reader arrowing through the days sees the same bubble a mouse would show them. Screen
 * readers do not need it either way: each circle's `aria-label` carries the date and the mood
 * whether or not the bubble is up.
 */
const tip = ref<{day: string; note: string; x: number; y: number} | null>(null);

function showTip(event: PointerEvent | FocusEvent, cell: Cell): void {
  const target = event.currentTarget as HTMLElement;
  const container = root.value;
  if (!container) {
    return;
  }
  const cellBox = target.getBoundingClientRect();
  const rootBox = container.getBoundingClientRect();
  tip.value = {
    day: cell.label,
    note: cellNote(cell),
    // Centred over the circle, sitting just above it.
    x: cellBox.left - rootBox.left + cellBox.width / 2,
    y: cellBox.top - rootBox.top,
  };
}

function hideTip(): void {
  tip.value = null;
}

/**
 * The scroller opens at its right-hand end.
 *
 * A year of months runs left to right, so the newest weeks — the ones the reader came to see, and
 * the only ones they can act on — sit off the right edge. Opening at the left showed last autumn
 * and left today three screens away. Scrolled with `behavior: 'auto'`: this is the starting
 * position, not a movement the reader made.
 */

async function scrollToLatest(): Promise<void> {
  if (props.mode !== 'range') {
    return;
  }
  // After the blocks exist, or the scroller has nothing to scroll to yet.
  await nextTick();
  // By index rather than by pixel: only a few blocks are rendered, so the scroller owns the
  // mapping from month to offset.
  plotArea.value?.scrollTo(Math.max(monthGroups.value.length - 1, 0), 'end');
}

onMounted(scrollToLatest);
// The period changing swaps the whole plot, so the new one starts at its own right-hand end too.
watch(() => [props.mode, props.periodStart, props.periodEnd], scrollToLatest);
</script>

<template>
  <!-- Named by its own heading rather than a duplicate aria-label, which reads the title twice. -->
  <section
    ref="root"
    class="mood-map"
    aria-labelledby="mood-map-title"
    @keydown="onKeydown"
    @focusin="onFocusIn"
  >
    <!--
      Described by every day rather than by the map, so it is spoken when focus lands on a circle —
      the moment the keys are worth knowing about, and for a reader who has no way to see the rest.
    -->
    <p :id="`mood-map-keys-${instanceId}`" class="sr-only">
      {{ $t('mood_map_keyboard_hint') }}
    </p>
    <!-- aria-hidden: assistive tech already gets this from each cell's own label. -->
    <transition name="mm-tip">
      <div
        v-if="tip"
        class="mm-tip"
        aria-hidden="true"
        :style="{left: `${tip.x}px`, top: `${tip.y}px`}"
      >
        {{ tip.day }}<span class="mm-tip-sub">{{ tip.note }}</span>
      </div>
    </transition>

    <header class="mm-head">
      <h3 id="mood-map-title" class="mm-title">
        {{ $t('mood_map_title') }}
      </h3>
      <p class="mm-count">
        {{ countLabel }}
      </p>
    </header>

    <!-- Week: seven fixed columns, so an untracked Tuesday still reads as a Tuesday. -->
    <div v-if="mode === 'week'" class="mm-week">
      <div
        v-for="(cell, index) in weekCells"
        :key="cell.key"
        class="mm-week-day"
      >
        <span class="mm-dow">{{ $t(weekdayKeys[index]) }}</span>
        <button
          v-bind="cellAttrs(cell)"
          class="mm-cell"
          :aria-label="cellTitle(cell)"
          :class="{
            'mm-cell--future': cell.isFuture,
            'mm-cell--moody': Boolean(cell.mood),
            'mm-cell--dimmed': isDimmed(cell),
            'mm-cell--selectable': isSelectable(cell),
          }"
          :style="cellStyle(cell)"
          @pointerenter="showTip($event, cell)"
          @pointerleave="hideTip"
          @pointercancel="hideTip"
          @focus="showTip($event, cell)"
          @blur="hideTip"
          @click="selectDay(cell)"
        />
      </div>
    </div>

    <!-- A trailing window: its own days, seven to a row, aligned on the weekday columns. -->
    <div v-else-if="mode === 'span'" class="mm-span">
      <span
        v-for="dayKey in weekdayKeys"
        :key="dayKey"
        class="mm-dow mm-dow--grid"
      >{{ $t(dayKey) }}</span>
      <button
        v-for="(cell, index) in spanCells"
        :key="cell.key"
        v-bind="cellAttrs(cell)"
        class="mm-cell"
        :aria-label="cellTitle(cell)"
        :class="{
          'mm-cell--future': cell.isFuture,
          'mm-cell--moody': Boolean(cell.mood),
          'mm-cell--dimmed': isDimmed(cell),
          'mm-cell--selectable': isSelectable(cell),
        }"
        :style="index === 0 ? {...cellStyle(cell), gridColumnStart: spanStartColumn} : cellStyle(cell)"
        @pointerenter="showTip($event, cell)"
        @pointerleave="hideTip"
        @pointercancel="hideTip"
        @focus="showTip($event, cell)"
        @blur="hideTip"
        @click="selectDay(cell)"
      />
    </div>

    <!--
      A block per calendar month, recycled rather than all rendered.

      The month is the unit of reuse: one block is a fixed-width grid of about thirty cells, so
      keeping only the visible handful in the DOM holds the cost flat however long someone has
      journalled. Rendering every block put a year at 365 cells and 1,095 pointer listeners, and
      a history of several years well past what a phone should be asked to lay out.

      The header's count is computed from `monthGroups`, never from what is rendered, so recycling
      cannot make the map understate what it covers.
    -->
    <q-virtual-scroll
      v-else
      ref="plotArea"
      class="mm-plotarea"
      :items="monthGroups"
      virtual-scroll-horizontal
      :virtual-scroll-item-size="MONTH_BLOCK_WIDTH"
      :virtual-scroll-slice-size="6"
    >
      <template #default="{item: group}">
        <div
          :key="group.key"
          class="mm-group"
        >
          <span class="mm-group-lab">{{ group.label }}</span>
          <div class="mm-daygrid">
            <button
              v-for="(cell, index) in cellsOf(group)"
              :key="cell.key"
              v-bind="cellAttrs(cell)"
              class="mm-cell"
              :aria-label="cellTitle(cell)"
              :class="{
                'mm-cell--future': cell.isFuture,
                'mm-cell--moody': Boolean(cell.mood),
                'mm-cell--dimmed': isDimmed(cell),
                'mm-cell--selectable': isSelectable(cell),
              }"
              :style="monthCellStyle(cell, index, group.startColumn)"
              @pointerenter="showTip($event, cell)"
              @pointerleave="hideTip"
              @pointercancel="hideTip"
              @focus="showTip($event, cell)"
              @blur="hideTip"
              @click="selectDay(cell)"
            />
          </div>
        </div>
      </template>
    </q-virtual-scroll>
  </section>
</template>

<style scoped lang="scss">
/**
 * The gap between one day's circle and the next: half the page's 12px step.
 *
 * The map is a dense field of small circles, and at the page's ordinary spacing a month read as a
 * loose grid rather than as one block. Stated here rather than as a token because none carries 6,
 * and stated once because `$mm-block-width` is derived from it — and `MONTH_BLOCK_WIDTH` in the
 * script above has to be `$mm-block-width + $mm-block-gap`, which nothing can check for us.
 */
$mm-day-gap: 6px;
/** Seven columns of circle and six gaps between them. */
$mm-block-width: 7 * 26px + 6 * $mm-day-gap;
/** The separation between one month block and the next. */
$mm-block-gap: 20px;

.mood-map {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  background: var(--Neutral-UI-neutral-ui-10);
  border-radius: var(--card-border-radius);
  padding: var(--Spacing-spacing-4);

  @include screen-sm {
    padding: var(--Spacing-spacing-6);
  }
}

.mm-tip {
  position: absolute;
  z-index: 20;
  // Anchored at the centre-top of its circle, then pulled up and back over its own box, clearing
  // the circle by enough to leave room for the pointer below.
  transform: translate(-50%, calc(-100% - 9px));
  pointer-events: none;
  white-space: nowrap;
  // The bubble's own measurements, from the design: no spacing or radius token carries 7, 11 or 10,
  // and a tooltip sized in the page's larger steps reads as a panel rather than a label.
  padding: 7px 11px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  // Inverted against the page, the usual tooltip treatment, and it flips with the scheme so the
  // bubble stays dark-on-light in one mode and light-on-dark in the other.
  background: var(--Neutral-UI-neutral-ui-100);
  color: var(--Neutral-UI-neutral-ui-0);
  box-shadow: 0 6px 18px rgb(32 40 46 / 22%);
}

// The pointer: a small square rotated onto its corner, half of it tucked behind the bubble.
.mm-tip::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -5px;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: var(--Neutral-UI-neutral-ui-100);
  transform: translateX(-50%) rotate(45deg);
}

// The mood sits under its date, lighter, so the date is what the eye takes first.
.mm-tip-sub {
  display: block;
  font-weight: 500;
  opacity: 0.66;
}

.mm-tip-enter-from,
.mm-tip-leave-to {
  opacity: 0;
}

.mm-tip-enter-active,
.mm-tip-leave-active {
  transition: opacity 0.13s;
}

.mm-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--Spacing-spacing-3);
  flex-wrap: wrap;
}

.mm-title {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.mm-count {
  @include font-metric-sm;
  color: var(--Neutral-UI-neutral-ui-60);
  margin: 0;
}

.mm-week {
  display: grid;
  // Fixed-width columns rather than `1fr`, so the week stays a compact row of circles at the left
  // of the card the way the design has it. Stretched to the card's width, seven `1fr` columns put
  // a circle every 160-odd pixels and the row stopped reading as a week at all.
  grid-template-columns: repeat(7, minmax(0, 44px));
  justify-content: start;
  gap: $mm-day-gap;
}

.mm-week-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.mm-dow {
  @include font-metric-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.mm-span {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 44px));
  justify-content: start;
  gap: $mm-day-gap;
}

// The weekday header row of the span grid: centred over its column rather than left-aligned like
// the week layout's labels, which sit above a single wide cell each.
.mm-dow--grid {
  text-align: center;
}

.mm-cell {
  display: block;
  width: 100%;
  max-width: 44px;
  aspect-ratio: 1;
  border-radius: 50%;
  // A circle is a `button` on the days that open something and a `span` on the rest, so the
  // button's own chrome comes off here and both draw the same shape.
  appearance: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  // A day the user did not log. The three states are told apart by treatment — soft fill, ring,
  // colour — rather than by depth of grey, because the mood tints are pinned pastels that carry
  // almost no luminance contrast on the light card (joyful is 1.03:1 against it). A grey heavy
  // enough to clear 3:1 on its own would sit *louder* than every mood on the map and turn the
  // empty days into the thing the eye lands on, which is backwards. The tooltip and each cell's
  // aria-label carry the meaning in words, so nothing depends on telling two greys apart.
  background: var(--Neutral-UI-neutral-ui-30);

  &--future {
    // Still to come: a ring and no fill, so it reads as an outline of a day rather than a quiet
    // one. Quieter than the untracked fill on purpose — the past is data, the future is not.
    background: transparent;
    box-shadow: inset 0 0 0 1px var(--Neutral-UI-neutral-ui-30);
  }

  // Pushed back while the mood filter is narrowing the list. Faded rather than hidden: the shape
  // of the period stays readable, and the days that match stand out of it.
  &--dimmed {
    opacity: 0.25;
  }

  &--selectable {
    cursor: pointer;
  }

  // Outside the circle rather than on it, so the ring does not read as a mood's own edge.
  &:focus-visible {
    outline: 2px solid var(--Neutral-UI-neutral-ui-100);
    outline-offset: 2px;
  }
}

.mm-plotarea {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

// The virtual scroller supplies its own flex container, so the old `.mm-plot` wrapper is gone.
.mm-plotarea :deep(.q-virtual-scroll__content) {
  display: flex;
  align-items: flex-start;
}

.mm-group {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
  // The whole pitch, and it has to be the block's own width: the day grid plus the gap to the
  // next block.
  //
  // That gap is padding and not a margin on purpose. The scroller reserves its runway from
  // `MONTH_BLOCK_WIDTH` before it has rendered anything, then corrects it against what the blocks
  // actually measure — and a margin sits outside that measurement. Carried as a margin, every
  // block scrolled past measured a gap short of what was reserved, the runway shrank as it was
  // traversed, and the far end of the scroll kept moving as it was approached.
  box-sizing: border-box;
  flex: 0 0 $mm-block-width + $mm-block-gap;
  padding-right: $mm-block-gap;
}

.mm-group-lab {
  @include font-metric-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.mm-daygrid {
  display: grid;
  // Seven days a row, so a month reads as weeks even without weekday labels.
  grid-template-columns: repeat(7, 1fr);
  gap: $mm-day-gap;

  .mm-cell {
    width: 22px;
    max-width: none;

    @include screen-sm {
      width: 26px;
    }
  }
}

</style>
